import { useCallback, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';

type ParamValue = string | number | boolean | undefined | null;

interface UseQueryParamsReturn {
  getParam: (key: string) => string | null;
  getParamAsNumber: (key: string, defaultValue?: number) => number;
  getParamAsBoolean: (key: string) => boolean;
  setParam: (key: string, value: ParamValue) => void;
  setParams: (params: Record<string, ParamValue>) => void;
  removeParam: (key: string) => void;
  clearParams: () => void;
  getAllParams: () => Record<string, string>;
}

/**
 * Хук для работы с URL query параметрами
 * Позволяет синхронизировать состояние фильтров и поиска с URL
 */
export function useQueryParams(): UseQueryParamsReturn {
  const [searchParams, setSearchParams] = useSearchParams();

  /**
   * Получить значение параметра как строку
   */
  const getParam = useCallback(
    (key: string): string | null => {
      return searchParams.get(key);
    },
    [searchParams]
  );

  /**
   * Получить значение параметра как число
   */
  const getParamAsNumber = useCallback(
    (key: string, defaultValue: number = 0): number => {
      const value = searchParams.get(key);
      if (!value) return defaultValue;
      const parsed = parseInt(value, 10);
      return isNaN(parsed) ? defaultValue : parsed;
    },
    [searchParams]
  );

  /**
   * Получить значение параметра как boolean
   */
  const getParamAsBoolean = useCallback(
    (key: string): boolean => {
      const value = searchParams.get(key);
      return value === 'true';
    },
    [searchParams]
  );

  /**
   * Установить один параметр
   */
  const setParam = useCallback(
    (key: string, value: ParamValue) => {
      setSearchParams((prev) => {
        const newParams = new URLSearchParams(prev);
        
        // Удаляем параметр если значение пустое/undefined/null
        if (value === undefined || value === null || value === '') {
          newParams.delete(key);
        } else {
          newParams.set(key, String(value));
        }
        
        return newParams;
      });
    },
    [setSearchParams]
  );

  /**
   * Установить несколько параметров за раз
   */
  const setParams = useCallback(
    (params: Record<string, ParamValue>) => {
      setSearchParams((prev) => {
        const newParams = new URLSearchParams(prev);
        
        Object.entries(params).forEach(([key, value]) => {
          if (value === undefined || value === null || value === '') {
            newParams.delete(key);
          } else {
            newParams.set(key, String(value));
          }
        });
        
        return newParams;
      });
    },
    [setSearchParams]
  );

  /**
   * Удалить параметр
   */
  const removeParam = useCallback(
    (key: string) => {
      setSearchParams((prev) => {
        const newParams = new URLSearchParams(prev);
        newParams.delete(key);
        return newParams;
      });
    },
    [setSearchParams]
  );

  /**
   * Очистить все параметры
   */
  const clearParams = useCallback(() => {
    setSearchParams(new URLSearchParams());
  }, [setSearchParams]);

  /**
   * Получить все параметры как объект
   */
  const getAllParams = useCallback((): Record<string, string> => {
    const params: Record<string, string> = {};
    searchParams.forEach((value, key) => {
      params[key] = value;
    });
    return params;
  }, [searchParams]);

  return useMemo(
    () => ({
      getParam,
      getParamAsNumber,
      getParamAsBoolean,
      setParam,
      setParams,
      removeParam,
      clearParams,
      getAllParams,
    }),
    [
      getParam,
      getParamAsNumber,
      getParamAsBoolean,
      setParam,
      setParams,
      removeParam,
      clearParams,
      getAllParams,
    ]
  );
}

